import {
  addElevationProfileAtom,
  elevationProfileDraftLayerIdsAtom,
  elevationProfileDraftPointsAtom,
  elevationProfileToolModeAtom,
  inferDominantLayerId,
  meshPickTargetsAtom,
  pointCloudTargetsAtom,
} from '@/atoms';
import { useFrame, useThree } from '@react-three/fiber';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { pickSurface } from './pickSurface';
import PointX from './PointX';

const DEFAULT_BUFFER = 1.0;
/** Click-to-finish radius in screen pixels. Matches AreaMeasuringTool. */
const CLOSE_THRESHOLD_PX = 20;

const CylinderLine = ({
  start,
  end,
  color,
  opacity = 0.8,
  radius = 0.005,
}: {
  start: THREE.Vector3;
  end: THREE.Vector3;
  color: string;
  opacity?: number;
  radius?: number;
}) => {
  const { midpoint, length, quaternion } = useMemo(() => {
    const mid = start.clone().add(end).multiplyScalar(0.5);
    const len = start.distanceTo(end);
    const dir = new THREE.Vector3().subVectors(end, start).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    return {
      midpoint: mid.toArray() as [number, number, number],
      length: len,
      quaternion: [quat.x, quat.y, quat.z, quat.w] as [number, number, number, number],
    };
  }, [start, end]);

  if (length < 1e-6) return null;
  return (
    <mesh position={midpoint} quaternion={quaternion} renderOrder={Number.MAX_SAFE_INTEGER - 100}>
      <cylinderGeometry args={[radius, radius, length, 8, 1]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} depthTest={false} />
    </mesh>
  );
};

/**
 * Polyline drawing tool for the elevation-profile feature. Active when
 * `elevationProfileToolModeAtom.phase === 'drawing'`.
 *
 * Interaction:
 *   - Left click on empty surface → adds a vertex.
 *   - Left click within ~20 px of the last placed vertex (≥2 vertices already
 *     placed) → finishes the polyline. A green ball appears on the last
 *     vertex when the cursor is within range so the user knows where to
 *     click. This mirrors `AreaMeasuringTool`'s "click near first vertex
 *     to close" UX, which is the proven pattern in this codebase.
 *   - Enter → also finishes (keyboard alternative).
 *   - Escape / right-click → cancels the in-progress polyline.
 *
 * Why not double-click? `@react-three/drei`'s `CameraControls` may consume
 * `dblclick` for camera focus, so we can't rely on it firing through to our
 * handler. Click-near-last-vertex is independent of camera-controls and
 * matches what the rest of the viewer already does.
 */
const ElevationProfileTool = ({ projectId }: { projectId?: string }) => {
  // Draft vertices live in a Jotai atom so an out-of-Canvas hint panel
  // (`<ElevationProfileDrawingHint>`) can read the count + render Done /
  // Cancel buttons. We keep a Vector3 view derived via useMemo for the
  // in-Canvas rendering and click-distance math.
  const [draftPoints, setDraftPoints] = useAtom(elevationProfileDraftPointsAtom);
  const setDraftLayerIds = useSetAtom(elevationProfileDraftLayerIdsAtom);
  const pickedPoints = useMemo(() => draftPoints.map((p) => new THREE.Vector3(...p)), [draftPoints]);
  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const [isNearLastVertex, setIsNearLastVertex] = useState(false);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);
  const meshTargets = useAtomValue(meshPickTargetsAtom);
  const [mode, setMode] = useAtom(elevationProfileToolModeAtom);
  const addProfile = useSetAtom(addElevationProfileAtom(projectId ?? ''));
  const drawing = mode.phase === 'drawing';

  // Ref mirror of the atom — event handlers / key listeners read this so
  // they always see the freshest array without restarting their effects
  // (and without React-impure tricks like calling side-effects from inside
  // a setState updater).
  const draftPointsRef = useRef<[number, number, number][]>([]);
  useEffect(() => {
    draftPointsRef.current = draftPoints;
  }, [draftPoints]);

  // Mirror of the layer-id-per-vertex atom — read at finish-time to infer
  // which point cloud the user picked from most. Same ref-mirror pattern
  // as draftPointsRef so handlers see the freshest array.
  const draftLayerIdsRef = useRef<(string | null)[]>([]);
  const draftLayerIds = useAtomValue(elevationProfileDraftLayerIdsAtom);
  useEffect(() => {
    draftLayerIdsRef.current = draftLayerIds;
  }, [draftLayerIds]);

  const finish = useCallback(
    (points: ReadonlyArray<[number, number, number]>, layerIds: ReadonlyArray<string | null>) => {
      if (!projectId || points.length < 2) return;
      const id = addProfile({
        polylinePoints: points.map((p) => [...p] as [number, number, number]),
        bufferDistance: DEFAULT_BUFFER,
        // Default to the layer the user picked from most often. They can
        // still change it via the panel's Source dropdown.
        sourceLayerId: inferDominantLayerId(layerIds),
      });
      setMode({ phase: 'panel', activeId: id });
    },
    [projectId, addProfile, setMode],
  );

  const tryFinish = useCallback(() => {
    const pts = draftPointsRef.current;
    if (pts.length >= 2) finish(pts, draftLayerIdsRef.current);
  }, [finish]);

  const cancel = useCallback(() => {
    setDraftPoints([]);
    setDraftLayerIds([]);
    setHoverPoint(null);
    setIsNearLastVertex(false);
    setMode({ phase: 'closed', activeId: null });
  }, [setMode, setDraftPoints, setDraftLayerIds]);

  // Reset internal + atom state any time the tool is deactivated externally
  // (panel opens after finish, user toggles the toolbar off, etc.).
  useEffect(() => {
    if (!drawing) {
      setDraftPoints([]);
      setDraftLayerIds([]);
      setHoverPoint(null);
      setIsNearLastVertex(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing]);

  // Project a world point through the camera to screen pixels — used for
  // "click near last vertex" detection.
  const getScreenDistance = useCallback(
    (clientX: number, clientY: number, worldPoint: THREE.Vector3) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const projected = worldPoint.clone().project(camera);
      const sx = ((projected.x + 1) / 2) * rect.width;
      const sy = ((-projected.y + 1) / 2) * rect.height;
      return Math.sqrt((clientX - rect.left - sx) ** 2 + (clientY - rect.top - sy) ** 2);
    },
    [camera, renderer],
  );

  // Hover: surface preview + near-last-vertex indicator.
  useEffect(() => {
    if (!drawing) return;
    const tmpVec = new THREE.Vector3();
    const handleMove = async (e: MouseEvent) => {
      const pts = draftPointsRef.current;
      // Near-last detection takes priority — show the green "click here to
      // finish" indicator and a finish-cursor instead of a vertex preview.
      if (pts.length >= 2) {
        const last = pts[pts.length - 1];
        const dist = getScreenDistance(e.clientX, e.clientY, tmpVec.set(last[0], last[1], last[2]));
        const near = dist < CLOSE_THRESHOLD_PX;
        setIsNearLastVertex(near);
        if (near) {
          setHoverPoint(null);
          renderer.domElement.style.cursor = 'pointer';
          return;
        }
      }

      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);
      if (hit) {
        setHoverPoint(hit.position.clone());
        renderer.domElement.style.cursor = 'crosshair';
      } else {
        setHoverPoint(null);
        renderer.domElement.style.cursor = 'default';
      }
    };
    renderer.domElement.addEventListener('pointermove', handleMove);
    return () => {
      renderer.domElement.removeEventListener('pointermove', handleMove);
      renderer.domElement.style.cursor = 'default';
    };
    // `isNearLastVertex` intentionally omitted — we read it inside but the
    // handler should not re-attach when it flips, the pointermove will
    // re-derive it on the next event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing, pcoTargets, meshTargets, camera, renderer, raycaster, getScreenDistance]);

  // Click-vs-drag detection so camera orbits don't accidentally place a vertex.
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!drawing) return;
    const dom = renderer.domElement;
    const DRAG_THRESHOLD = 5;

    const handleDown = (e: MouseEvent) => {
      pointerDownPos.current = { x: e.clientX, y: e.clientY };
    };
    const handleUp = async (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (!pointerDownPos.current) return;
      const dx = e.clientX - pointerDownPos.current.x;
      const dy = e.clientY - pointerDownPos.current.y;
      pointerDownPos.current = null;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) return;

      // Click near the last vertex finishes the polyline rather than placing
      // a new (almost-coincident) vertex.
      const pts = draftPointsRef.current;
      if (pts.length >= 2) {
        const last = pts[pts.length - 1];
        const lastVec = new THREE.Vector3(last[0], last[1], last[2]);
        const dist = getScreenDistance(e.clientX, e.clientY, lastVec);
        if (dist < CLOSE_THRESHOLD_PX) {
          tryFinish();
          return;
        }
      }

      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);
      if (!hit) return;
      setDraftPoints((prev) => [...prev, [hit.position.x, hit.position.y, hit.position.z] as [number, number, number]]);
      // Record the source layer id (null for mesh hits) so the panel can
      // pre-select the most-frequent point cloud as the default sample
      // source. Kept in lock-step with draftPoints.
      setDraftLayerIds((prev) => [...prev, hit.layerId]);
      setHoverPoint(null);
    };
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    };
    dom.addEventListener('pointerdown', handleDown);
    dom.addEventListener('pointerup', handleUp);
    dom.addEventListener('contextmenu', handleContextMenu);
    return () => {
      dom.removeEventListener('pointerdown', handleDown);
      dom.removeEventListener('pointerup', handleUp);
      dom.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [drawing, pcoTargets, meshTargets, camera, renderer, raycaster, tryFinish, cancel, getScreenDistance]);

  // Keyboard: Enter finishes, Escape cancels.
  useEffect(() => {
    if (!drawing) return;
    const handleKey = (e: KeyboardEvent) => {
      // Ignore key events from form fields so typing in the panel's name
      // input or buffer slider doesn't accidentally submit/cancel a draw.
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        tryFinish();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [drawing, tryFinish, cancel]);

  // Marker scale follows the camera distance so vertex dots stay readable.
  const calculateScale = useCallback(
    (point: THREE.Vector3) => {
      const d = camera.position.distanceTo(point);
      const pr = renderer.getPixelRatio();
      const height = renderer.domElement.clientHeight * pr;
      if (camera.type === 'OrthographicCamera') {
        const oc = camera as THREE.OrthographicCamera;
        const worldScreenHeight = Math.abs(oc.top - oc.bottom) / oc.zoom;
        return (20 * worldScreenHeight) / height;
      }
      const pc = camera as THREE.PerspectiveCamera;
      const vFOV = (pc.getEffectiveFOV() * Math.PI) / 180;
      const worldScreenHeight = 2 * d * Math.tan(vFOV / 2);
      return (20 * worldScreenHeight) / height;
    },
    [camera, renderer],
  );

  const pointGroupRefs = useRef<(THREE.Group | null)[]>([]);
  const hoverGroupRef = useRef<THREE.Group | null>(null);

  useFrame(() => {
    pickedPoints.forEach((point, i) => {
      const g = pointGroupRefs.current[i];
      if (g) g.scale.setScalar(calculateScale(point));
    });
    if (hoverGroupRef.current && hoverPoint) {
      hoverGroupRef.current.scale.setScalar(calculateScale(hoverPoint));
    }
  });

  if (!drawing) return null;

  // Last-segment preview from the last placed vertex to the hover point —
  // gives the user a live sense of where the polyline will go.
  const lastVertex = pickedPoints.length > 0 ? pickedPoints[pickedPoints.length - 1] : null;
  const previewLast = lastVertex && hoverPoint && !isNearLastVertex ? lastVertex : null;

  return (
    <>
      {hoverPoint && !isNearLastVertex && (
        <group ref={hoverGroupRef} position={hoverPoint.toArray()} scale={calculateScale(hoverPoint)}>
          <PointX scale={1} position={new THREE.Vector3()} />
        </group>
      )}

      {pickedPoints.map((point, i) => (
        <group
          key={i}
          ref={(el) => {
            pointGroupRefs.current[i] = el;
          }}
          position={point.toArray()}
          scale={calculateScale(point)}>
          <PointX scale={1} position={new THREE.Vector3()} />
        </group>
      ))}

      {pickedPoints.slice(0, -1).map((p, i) => (
        <CylinderLine key={`seg-${i}`} start={p} end={pickedPoints[i + 1]} color='deepskyblue' />
      ))}

      {previewLast && hoverPoint && (
        <CylinderLine start={previewLast} end={hoverPoint} color='deepskyblue' opacity={0.4} />
      )}

      {/* "Click here to finish" indicator — green ball on the last vertex
          when the cursor is within the close threshold. Mirrors the
          AreaMeasuringTool close-loop indicator. */}
      {isNearLastVertex && lastVertex && (
        <mesh position={lastVertex.toArray()} renderOrder={Number.MAX_SAFE_INTEGER}>
          <sphereGeometry args={[calculateScale(lastVertex) * 0.4, 16, 16]} />
          <meshBasicMaterial color='lime' transparent opacity={0.7} depthTest={false} />
        </mesh>
      )}
    </>
  );
};

export default ElevationProfileTool;
